import React, { useEffect, useState } from 'react';
import { Alert, Box, TextField, Typography, IconButton, MenuItem, Button } from '@mui/material';
import { useNode, useEditor } from '@craftjs/core';
import { Plus, Trash, DotsSixVertical } from '@phosphor-icons/react';
import api from '../../../../utils/api';
import { useBusiness } from '../../../../context/BusinessContext';

const blockWrapperSx = {
  p: 1.5,
  mb: 1,
  borderRadius: 1,
  border: '1px dashed',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' }
};

function createColumnNodeTree(query, width) {
  const node = query.parseSerializedNode({
    type: { resolvedName: 'ColumnBlock' },
    isCanvas: true,
    props: { width },
    nodes: [],
    linkedNodes: {},
    parent: null,
    displayName: 'ColumnBlock'
  }).toNode();

  return {
    rootNodeId: node.id,
    nodes: { [node.id]: node }
  };
}

function BlockWrapper({ children, id, label }) {
  const { connectors: { connect, drag } } = useNode();
  const { actions } = useEditor();
  return (
    <Box
      ref={(dom) => dom && connect(dom)}
      sx={{
        ...blockWrapperSx,
        cursor: 'default'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <Box
          ref={(dom) => dom && drag(dom)}
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            flexShrink: 0,
            cursor: 'grab',
            color: 'text.secondary',
            '&:active': { cursor: 'grabbing' }
          }}
          aria-label="Drag to move block"
        >
          <DotsSixVertical size={18} weight="bold" aria-hidden />
          <Typography component="span" variant="caption" fontWeight={600} color="text.secondary">
            {label}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }} />
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            actions.delete(id);
          }}
          color="error"
        >
          <Trash size={16} />
        </IconButton>
      </Box>
      <Box>{children}</Box>
    </Box>
  );
}

export function HeadingBlock({ text = '', level = 2 }) {
  const { id, actions: { setProp } } = useNode();
  return (
    <BlockWrapper id={id} label="Heading">
      <TextField
        fullWidth
        size="small"
        placeholder="Heading"
        value={text}
        onChange={(e) => setProp((p) => { p.text = e.target.value; })}
        variant="standard"
        InputProps={{
          sx: { fontSize: level === 1 ? '1.5rem' : level === 3 ? '1rem' : '1.25rem', fontWeight: 600 }
        }}
      />
    </BlockWrapper>
  );
}
HeadingBlock.craft = { displayName: 'HeadingBlock', props: { text: '', level: 2 } };

export function ParagraphBlock({ text = '' }) {
  const { id, actions: { setProp } } = useNode();
  return (
    <BlockWrapper id={id} label="Paragraph">
      <TextField
        fullWidth
        size="small"
        placeholder="Paragraph"
        multiline
        minRows={2}
        value={text}
        onChange={(e) => setProp((p) => { p.text = e.target.value; })}
        variant="standard"
      />
    </BlockWrapper>
  );
}
ParagraphBlock.craft = { displayName: 'ParagraphBlock', props: { text: '' } };

export function ImageBlock({ url = '', alt = '' }) {
  const { id, actions: { setProp } } = useNode();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.business?._id;
  const [uploadError, setUploadError] = useState('');

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError('');

    const isValidType = ['image/png', 'image/jpeg', 'image/jpg'].includes(file.type);
    const isValidSize = file.size <= 2 * 1024 * 1024;
    if (!isValidType) {
      setUploadError('Please upload a PNG or JPEG image.');
      event.target.value = '';
      return;
    }
    if (!isValidSize) {
      setUploadError('Agreement images must be 2MB or smaller.');
      event.target.value = '';
      return;
    }
    if (!businessId) {
      setUploadError('Business not found. Please refresh and try again.');
      event.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('businessId', businessId);

    try {
      const { data } = await api.post('/api/agreements/images/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProp((p) => {
        p.url = data?.url || '';
        p.fileSize = data?.fileSize || file.size;
      });
    } catch (error) {
      setUploadError(error.response?.data?.message || 'Failed to upload image.');
    } finally {
      event.target.value = '';
    }
  };

  return (
    <BlockWrapper id={id} label="Image">
      <Box sx={{ mb: 1.25 }}>
        <Button variant="outlined" component="label" size="small" sx={{ textTransform: 'none' }}>
          {url ? 'Replace image' : 'Upload image'}
          <input type="file" hidden accept="image/png,image/jpeg" onChange={handleUpload} />
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          PNG or JPG only, maximum 2MB.
        </Typography>
        {uploadError && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setUploadError('')}>
            {uploadError}
          </Alert>
        )}
      </Box>
      {url && (
        <Box sx={{ mb: 1 }}>
          <Box component="img" src={url} alt={alt || ''} sx={{ maxWidth: '100%', maxHeight: 180, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
        </Box>
      )}
    </BlockWrapper>
  );
}
ImageBlock.craft = { displayName: 'ImageBlock', props: { url: '', alt: '' } };

export function DividerBlock() {
  const { id } = useNode();
  return (
    <BlockWrapper id={id} label="Divider">
      <Box sx={{ height: 1, bgcolor: 'divider', my: 0.5 }} />
    </BlockWrapper>
  );
}
DividerBlock.craft = { displayName: 'DividerBlock', props: {} };

export function RowBlock({ children, columns = 2 }) {
  const { id, actions: { setProp } } = useNode();
  const { actions, query } = useEditor();
  const columnsCount = Math.max(1, Math.min(4, Number(columns) || 1));

  useEffect(() => {
    if (columnsCount !== columns) {
      setProp((p) => { p.columns = columnsCount; });
    }
  }, [columns, columnsCount, setProp]);

  useEffect(() => {
    const current = query.node(id).get();
    if (!current) return;
    let childIds = Array.isArray(current.data.nodes) ? [...current.data.nodes] : [];

    if (childIds.length < columnsCount) {
      const width = Number((100 / columnsCount).toFixed(1));
      const toAdd = columnsCount - childIds.length;
      for (let i = 0; i < toAdd; i += 1) {
        const tree = createColumnNodeTree(query, width);
        actions.addNodeTree(tree, id);
      }
      childIds = Array.isArray(query.node(id).get().data.nodes) ? [...query.node(id).get().data.nodes] : [];
    }

    if (childIds.length > columnsCount) {
      childIds.slice(columnsCount).forEach((childId) => actions.delete(childId));
      childIds = Array.isArray(query.node(id).get().data.nodes) ? [...query.node(id).get().data.nodes] : [];
    }

    const width = Number((100 / columnsCount).toFixed(1));
    childIds.slice(0, columnsCount).forEach((childId) => {
      const childNode = query.node(childId).get();
      const currentWidth = Number(childNode?.data?.props?.width) || 0;
      if (Math.abs(currentWidth - width) > 0.05) {
        actions.setProp(childId, (p) => { p.width = width; });
      }
    });
  }, [actions, columnsCount, id, query]);

  return (
    <BlockWrapper id={id} label="Row">
      <TextField
        select
        size="small"
        label="Columns"
        value={columnsCount}
        onChange={(e) => {
          const next = Math.max(1, Math.min(4, Number(e.target.value) || 1));
          setProp((p) => { p.columns = next; });
        }}
        sx={{ width: 120, mb: 1 }}
      >
        <MenuItem value={1}>1 column</MenuItem>
        <MenuItem value={2}>2 columns</MenuItem>
        <MenuItem value={3}>3 columns</MenuItem>
        <MenuItem value={4}>4 columns</MenuItem>
      </TextField>
      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          alignItems: 'stretch',
          gap: 2,
          minHeight: 96
        }}
      >
        {children || (
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              minHeight: 72,
              color: 'text.secondary'
            }}
          >
            <Plus size={22} aria-hidden />
            <Typography variant="body2" textAlign="center">
              Drag block here
            </Typography>
          </Box>
        )}
      </Box>
    </BlockWrapper>
  );
}
RowBlock.craft = { displayName: 'RowBlock', props: { columns: 2 } };

export function ColumnBlock({ width = 50, children }) {
  const { connectors: { connect } } = useNode();
  void width;
  return (
    <Box
      ref={(dom) => dom && connect(dom)}
      sx={{
        flex: { xs: '1 1 100%', md: '1 1 0' },
        minWidth: 0,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'grey.50',
        p: 1.25,
        minHeight: 88
      }}
    >
      {children || (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            minHeight: 72,
            py: 1,
            color: 'text.secondary'
          }}
        >
          <Plus size={22} aria-hidden />
          <Typography variant="body2" textAlign="center">
            Drag block here
          </Typography>
        </Box>
      )}
    </Box>
  );
}
ColumnBlock.craft = {
  displayName: 'ColumnBlock',
  props: { width: 50 },
  rules: {
    canDrag: () => false
  }
};
